import React, { useEffect, useState } from "react"
import { Modal, Button, Select, Flex, Input } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { CheckOutlined, CloseCircleOutlined, CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { createCategory, getCategories } from "store/trips"
import styles from "./index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import CustomDrawer from "ui/Drawer"

const EditCategoryModal = ({ visible, onClose, onSave, currentCategory, loading }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  // Backend-keyed categories ([{ id, name }]); the selected value is the numeric id sent
  // as category_ids, matching the wizard's Details step.
  const categories = useSelector((state) => state.trips.categories.data?.categories || [])
  const [selectedCategory, setSelectedCategory] = useState(currentCategory ?? null)
  const [searchValue, setSearchValue] = useState("")
  const [isCreatingInline, setIsCreatingInline] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState("")
  const [isCreatingLoading, setIsCreatingLoading] = useState(false)

  // Fetch the category list when the modal opens (if not already loaded).
  useEffect(() => {
    if (visible && categories.length === 0) dispatch(getCategories())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // Re-seed the selection from the trip being edited (the modal instance is reused
  // across rows). Kept independent of the category fetch so a late-arriving list
  // doesn't wipe a selection the user already made.
  useEffect(() => {
    if (!visible) return
    setSelectedCategory(currentCategory ?? null)
    setSearchValue("")
    setIsCreatingInline(false)
    setNewCategoryName("")
  }, [visible, currentCategory])

  const handleSave = () => {
    onSave(selectedCategory)
  }

  const handleCancel = () => {
    setSelectedCategory(currentCategory || null)
    setSearchValue("")
    setIsCreatingInline(false)
    setNewCategoryName("")
    onClose()
  }

  const handleInlineSave = async () => {
    const name = newCategoryName.trim()
    if (!name) return
    setIsCreatingLoading(true)
    try {
      const result = await dispatch(createCategory(name))
      const newId = result?.data?.category?.id
      if (newId) setSelectedCategory(newId)
      setIsCreatingInline(false)
      setNewCategoryName("")
    } finally {
      setIsCreatingLoading(false)
    }
  }

  const filteredCategories = categories.filter((category) => category.name.toLowerCase().includes(searchValue.toLowerCase()))

  const dropdownRender = (menu) => (
    <div>
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={styles.searchInput}
        />
      </div>
      {menu}
      {isCreatingInline ? (
        <Flex align="center" gap={8} className={styles.inlineCreateRow}>
          <Input
            autoFocus
            placeholder="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onPressEnter={handleInlineSave}
            className={styles.inlineCreateInput}
            disabled={isCreatingLoading}
          />
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={handleInlineSave}
            loading={isCreatingLoading}
            disabled={!newCategoryName.trim()}
          />
          <Button
            type="text"
            icon={<CloseCircleOutlined />}
            onClick={() => { setIsCreatingInline(false); setNewCategoryName("") }}
            disabled={isCreatingLoading}
          />
        </Flex>
      ) : (
        <Flex align="center" justify="center">
          <Button type="link" icon={<PlusOutlined />} onClick={() => setIsCreatingInline(true)}>
            Create Category
          </Button>
        </Flex>
      )}
    </div>
  )

  const formContent = (
    <>
      <div className={styles.formGroup}>
        <LabeledField labelKey={t("equipment.edit_category_modal.category_label")}>
          <Select
            placeholder={t("equipment.edit_category_modal.select_category_placeholder")}
            value={selectedCategory}
            onChange={setSelectedCategory}
            className={styles.select}
            size="large"
            dropdownRender={dropdownRender}
            showSearch={false}
            options={filteredCategories.map((category) => ({
              value: category.id,
              label: category.name
            }))}
          />
        </LabeledField>
      </div>

      <Flex align="center" justify="space-between" gap={10} className={styles.editCategoryModalButtons}>
        <Button size="large" color="primary" variant="outlined" onClick={handleCancel} disabled={loading}>
          {t("equipment.edit_category_modal.cancel_button")}
        </Button>
        <Button size="large" color="primary" variant="solid" onClick={handleSave} disabled={selectedCategory == null} loading={loading}>
          {t("equipment.edit_category_modal.changes_button")}
        </Button>
      </Flex>
    </>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={visible} setOpen={handleCancel} title={t("equipment.edit_category_modal.title")}>
          <div className={styles.container}>{formContent}</div>
        </CustomDrawer>
      ) : (
        <Modal
          open={visible}
          onCancel={handleCancel}
          closeIcon={<CloseOutlined className={styles.closeIcon} />}
          footer={null}
          width={400}
          className={styles.modal}
          centered
          maskClosable={false}
        >
          <div className={styles.container}>
            <h2 className={styles.title}>{t("equipment.edit_category_modal.title")}</h2>
            {formContent}
          </div>
        </Modal>
      )}
    </>
  )
}

EditCategoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool
}

EditCategoryModal.defaultProps = {
  currentCategory: null,
  loading: false
}

export default EditCategoryModal
